'use client'

import { useRouter } from 'next/navigation'
import { Globe } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { setLocale } from '@/app/actions/set-locale'
import { useTranslations } from '@/lib/i18n/client'
import {
  SUPPORTED_LOCALES,
  LOCALE_LABELS,
  LOCALE_SHORT,
  LOCALE_FLAGS,
  type Locale,
} from '@/lib/i18n/locales'

export function LanguageSelector({ variant = 'header' }: { variant?: 'header' | 'mobile' }) {
  const router = useRouter()
  const { locale: currentLocale, t } = useTranslations()

  async function handleSelect(locale: Locale) {
    if (locale === currentLocale) return
    await setLocale(locale)
    router.refresh()
  }

  const triggerClass =
    variant === 'mobile'
      ? 'flex min-h-12 items-center gap-1.5 rounded-lg px-3 py-3 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white'
      : 'flex items-center gap-1.5 rounded-lg px-2.5 py-2.5 text-xs font-medium text-slate-300 transition-colors hover:text-white'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={t.common.changeLanguage} className={triggerClass}>
          <Globe className="h-4 w-4" aria-hidden="true" />
          {LOCALE_SHORT[currentLocale]}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === 'mobile' ? 'start' : 'end'}
        className="min-w-[140px]"
      >
        {SUPPORTED_LOCALES.map((loc) => (
          <DropdownMenuItem
            key={loc}
            onClick={() => handleSelect(loc)}
            className={`min-h-11 px-3 ${loc === currentLocale ? 'bg-slate-50 font-medium' : ''}`}
          >
            <span className="mr-2">{LOCALE_FLAGS[loc]}</span>
            {LOCALE_LABELS[loc]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
